import { useCertificateNormsQuery } from "graphqlBase/certificateNorm/__generated__/certificateNorms";
import { certificateToReplaceIdAtom } from "pages/UploadCertificate/state/uploadCertificateStateBase";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";

/**
 * Returns the exclusivity family of a norm based on its name.
 * - "4105": VDE-AR-N 4105 family (any version)
 * - "4110plus": VDE-AR-N 4110 / 4120 / 4130 families (any version)
 * - "other": no exclusivity restrictions
 *
 * This function intentionally uses norm name substrings rather than hardcoded IDs
 * so that new versions/revisions added to the backend are handled automatically.
 */
export const getNormFamily = (normName: string): "4105" | "4110plus" | "other" => {
  if (normName.includes("4105")) return "4105";
  if (["4110", "4120", "4130"].some((n) => normName.includes(n))) return "4110plus";
  return "other";
};

/**
 * Shared availability logic for the norm selector so that the "add norm" button
 * and the norm dropdown apply identical rules. Returns a per-norm `isNormEnabled`
 * predicate and `hasSelectableNorms`, which is false when no further norm can be
 * added given the current selection (so the trigger button can be disabled).
 */
export const useNormAvailability = (normBridgeIds: string[]) => {
  const { data: normsData } = useCertificateNormsQuery();
  const certificateToBeReplaced = !!useRecoilValue(certificateToReplaceIdAtom);
  const isGridformingUnitRelated = useLocation().pathname.includes("/upload-grid-forming-certificate");

  // Build both lookup maps in a single pass over the API data:
  // - normIdToName:       norm ID → norm name
  // - issueDateIdToNormId: issue date ID → parent norm ID
  const { normIdToName, issueDateIdToNormId } = useMemo(() => {
    const normIdToName = new Map<string, string>();
    const issueDateIdToNormId = new Map<string, string>();
    normsData?.certificateNorms?.items?.forEach((norm) => {
      normIdToName.set(norm.id, norm.certificateNormName);
      norm.certificateNormBridges.forEach((bridge) => {
        issueDateIdToNormId.set(bridge.certificateNormIssueDate.id, norm.id);
      });
    });
    return { normIdToName, issueDateIdToNormId };
  }, [normsData]);

  // Which norm IDs are already represented in the current selection
  const selectedNormIds = useMemo(
    () => new Set(normBridgeIds.map((id) => issueDateIdToNormId.get(id)).filter((id): id is string => !!id)),
    [normBridgeIds, issueDateIdToNormId]
  );

  // Which exclusivity families are already selected
  const selectedFamilies = useMemo(
    () => new Set(Array.from(selectedNormIds).map((id) => getNormFamily(normIdToName.get(id) ?? ""))),
    [selectedNormIds, normIdToName]
  );

  /**
   * Returns true if a norm can still be selected given current selections.
   * Rules:
   * - Only one version per norm family (e.g. not 4110:2018-11 + 4110:2023-09)
   * - 4105 and 4110/4120/4130 are mutually exclusive
   * - 4105 is not available in certificate-replacement flows
   */
  const isNormEnabled = useCallback(
    (id: string): boolean => {
      if (selectedNormIds.has(id)) return false;
      const name = normIdToName.get(id) ?? "";
      const family = getNormFamily(name);
      if (certificateToBeReplaced && family === "4105") return false;
      if (family === "4105" && selectedFamilies.has("4110plus")) return false;
      if (family === "4110plus" && selectedFamilies.has("4105")) return false;
      return true;
    },
    [normIdToName, selectedNormIds, selectedFamilies, certificateToBeReplaced]
  );

  // A norm is hidden entirely in grid-forming flows when it belongs to the 4105 family.
  const isNormHidden = useCallback(
    (id: string): boolean => isGridformingUnitRelated && getNormFamily(normIdToName.get(id) ?? "") === "4105",
    [isGridformingUnitRelated, normIdToName]
  );

  // True when at least one norm remains that is neither hidden nor disabled.
  const hasSelectableNorms = useMemo(
    () => (normsData?.certificateNorms?.items ?? []).some((norm) => !isNormHidden(norm.id) && isNormEnabled(norm.id)),
    [normsData, isNormHidden, isNormEnabled]
  );

  return { isNormEnabled, isNormHidden, hasSelectableNorms };
};
